'use client'

import { useMsal as azureUseMsal } from '@azure/msal-react'
import Cookies from 'js-cookie'
import { AlertCircleIcon } from 'lucide-react'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import type React from 'react'
import { type PropsWithChildren, useCallback } from 'react'

import { Button, useToast } from '@/components/ui/'
import { useMsal } from '@/contexts/msalProvider'
import MicrosoftIcon from '@/public/icons/microsoft.svg'
import { authService } from '@/services/http/authService'
import {
  AUTH_COOKIE,
  MICROSOFT_HOME_ACCOUNT_COOKIE,
  USER_INFO_COOKIE,
} from '@/utils/constants'
import { generateRoutePath } from '@/utils/routes/generateRoutePath'

export const LoginContainerHeader: React.FC = () => {
  const pathname = usePathname()
  const titleMapper: { [key: string]: string } = {
    [generateRoutePath('login')]: 'Entrar na sua conta',
    [generateRoutePath('loginByRegistration')]: 'Entrar na sua conta',
    [generateRoutePath('verifyCode')]: 'Entrar na sua conta',
    [generateRoutePath('changePassword')]: 'Altere sua senha',
    [generateRoutePath('changeEmail')]: 'Cadastre um email',
    [generateRoutePath('resetPassword')]: 'Alteração de senha',
    [generateRoutePath('resetPassword.token', {
      params: { token: '[token]' },
    }).replace('[token]', '[^/]+')]: 'Altere sua senha',
  }

  const subtitleMapper: { [key: string]: string } = {
    [generateRoutePath('changeEmail')]:
      'O email será utilizado para o envio de notificações e para a recuperação de senha, se necessário. ',
  }

  const title = Object.keys(titleMapper).find((key) =>
    new RegExp(`^${key}$`).test(pathname),
  )

  const subtitle = subtitleMapper[pathname]

  return (
    <div className="mb-10 flex w-full flex-col items-center gap-10">
      <div className="relative h-24 w-48">
        <Image
          src="/images/logos/avantti-purple.png"
          alt="Purple Logo Avantti"
          fill
          sizes="100%"
        />
      </div>

      <div className="flex flex-col items-center gap-3">
        <h1 className="text-2xl font-bold text-system-text-secondary">
          {titleMapper[title || '']}
        </h1>

        {subtitle && (
          <p className="text-center text-sm text-system-text-secondary">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  )
}

export const LoginContainerFooter: React.FC = () => {
  const router = useRouter()
  const { instance } = azureUseMsal()
  const { loadingMsalConfig, hasSso } = useMsal()
  const { toast } = useToast()

  const pathname = usePathname()
  const routesToShow = [
    generateRoutePath('login'),
    generateRoutePath('loginByRegistration'),
  ]

  const handleMicrosoftLogin = useCallback(async () => {
    try {
      await instance.handleRedirectPromise()

      const result = await instance.loginPopup({
        scopes: ['openid', 'profile'],
      })
      // save homeAccountId for when you log out
      Cookies.set(MICROSOFT_HOME_ACCOUNT_COOKIE, result.account.homeAccountId)

      const { token, user } = await authService.ssoLogin({
        idToken: result.idToken,
      })

      Cookies.set(AUTH_COOKIE, token)
      Cookies.set(USER_INFO_COOKIE, JSON.stringify(user))
      router.push(generateRoutePath('home'))
    } catch {
      toast({
        title: 'Falha ao tentar fazer login com Microsoft',
        variant: 'destructive',
        icon: AlertCircleIcon,
      })
    }
  }, [instance, router, toast])

  if (!routesToShow.includes(pathname)) {
    return null
  }

  return (
    <div className="mt-20 flex w-full flex-col items-center gap-10">
      <div className="flex w-full items-center gap-4">
        <hr className="w-full border-system-text-disabled opacity-40" />
        <span className="text-sm text-system-text-primary">Ou</span>
        <hr className="w-full border-system-text-disabled opacity-40" />
      </div>

      <Button
        variant="social"
        type="button"
        className="w-full"
        disabled={loadingMsalConfig || !hasSso}
        onClick={handleMicrosoftLogin}
      >
        <Image
          src={MicrosoftIcon}
          alt="Microsoft Icon"
          width={20}
          height={20}
        />
        Continue com Microsoft
      </Button>
    </div>
  )
}

export const LoginContainer: React.FC<PropsWithChildren> = ({ ...props }) => (
  <div
    className="flex flex-col items-center justify-center px-20 max-sm:w-full max-sm:px-8 sm:w-7/12 md:w-6/12 lg:w-5/12"
    {...props}
  />
)
